import { createFileRoute, Link } from "@tanstack/react-router";
import { PenLine, BookOpen, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { CITATION_STYLES } from "@/lib/citation-styles";

export const Route = createFileRoute("/education/writing/")({
  head: () => ({
    meta: [
      {
        title:
          "Legal Writing Academy — OSCOLA, Harvard, APA 7 & SA Citation | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Plain-English legal writing modules for South African LLB students: OSCOLA, Harvard, APA 7 and South African legal citation, with AI-powered citation and footnote tools.",
      },
      { property: "og:title", content: "Legal Writing Academy" },
      {
        property: "og:description",
        content:
          "Master OSCOLA, Harvard, APA 7 and South African legal citation — with AI citation and footnote assistance.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://nkadimengdannylcs.com/education/writing" },
    ],
  }),
  component: WritingAcademy,
});

function WritingAcademy() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Education · Legal Writing Academy
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Write like a lawyer — cite like a scholar
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Module-by-module guides to the four citation styles you'll meet at
            UNISA and in practice, plus AI-assisted tools to generate clean
            citations and footnotes from your own drafts.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/education/legal-article-guide"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-medium text-navy hover:bg-gold/90"
            >
              <BookOpen className="size-4" /> Standing guidelines — Legal Article Writing Guide
            </Link>
            <Link
              to="/education/writing/tools"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
            >
              <Sparkles className="size-4" /> Open the citation & footnote tools
            </Link>
            <Link
              to="/education/writing/article-editor"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              Article editor · download PDF
            </Link>
            <Link
              to="/education/writing/bibliography"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              Bibliography &amp; references generator
            </Link>
            <Link
              to="/education/writing/templates"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              Writing templates
            </Link>
            <Link
              to="/education"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              ← Back to Education hub
            </Link>
          </div>
        </div>
      </section>

      <section className="container-narrow py-12 md:py-16">
        <div className="mb-8 flex items-center gap-3 text-navy">
          <PenLine className="size-5 text-gold" />
          <h2 className="font-display text-2xl md:text-3xl">Citation styles</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {CITATION_STYLES.map((s) => (
            <Link
              key={s.slug}
              to="/education/writing/$style"
              params={{ style: s.slug }}
              className="group rounded-lg border border-border bg-card p-6 transition hover:border-gold hover:shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">
                    {s.shortName}
                  </p>
                  <h3 className="mt-1 font-display text-xl text-navy">{s.name}</h3>
                </div>
                <ArrowRight className="mt-1 size-5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-navy" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{s.summary}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                <span className="font-medium text-navy">Best for:</span> {s.audience}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-narrow py-12 md:py-16">
          <div className="mb-6 flex items-center gap-3 text-navy">
            <BookOpen className="size-5 text-gold" />
            <h2 className="font-display text-2xl md:text-3xl">What you'll learn</h2>
          </div>
          <ul className="grid gap-3 text-sm text-muted-foreground md:grid-cols-2">
            <li className="rounded border border-border bg-card p-4">
              <strong className="text-navy">Footnotes vs in-text:</strong> when
              to use a footnote (OSCOLA, SA) and when to use a parenthetical
              (Harvard, APA 7).
            </li>
            <li className="rounded border border-border bg-card p-4">
              <strong className="text-navy">South African case citation:</strong>{" "}
              party names, volume, law report series, court abbreviations, pinpoint
              paragraphs.
            </li>
            <li className="rounded border border-border bg-card p-4">
              <strong className="text-navy">Statutes & Government Gazettes:</strong>{" "}
              Acts, Regulations, GN/GG references, currency of legislation.
            </li>
            <li className="rounded border border-border bg-card p-4">
              <strong className="text-navy">Bibliography discipline:</strong>{" "}
              alphabetical ordering, hanging indents, categorised reference lists.
            </li>
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-narrow py-10 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-gold" />
            <span>
              Outputs from the AI citation and footnote tools are educational
              only. Always verify every authority against the primary source
              (gov.za, the official law report or the publisher's record) before
              submitting work or relying on it in practice.
            </span>
          </p>
        </div>
      </section>
    </>
  );
}